import { ChevronLeft, ChevronRight, CalendarClock, CalendarRange, Clock, CalendarDays, CalendarOff, Info } from 'lucide-react';
import FilterBar from './FilterBar.jsx';
import DatePicker from './DatePicker.jsx';

/**
 * Barre de contrôle des rapports — panneau carte sticky, 2 rangées alignées.
 * Contrôles regroupés en segmented-controls (Période / Granularité / Affichage /
 * Périmètre) + filtre facetté partagé (scope `schedule`).
 *
 * Sticky top:0 (cf. mémoire sticky-toolbar-offset).
 */

const PERIODS = [
  { key: 'day', label: 'Jour' },
  { key: 'week', label: 'Semaine' },
  { key: 'month', label: 'Mois' },
  { key: 'quarter', label: 'Trimestre' },
];

const SCOPE_LABELS = { self: 'Moi', team: 'Mon équipe', all: 'Tout le monde' };

const CTRL_H = 32;

/** Champ (select/date) au même gabarit que les segmented. */
const field = {
  height: CTRL_H,
  padding: '0 10px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--glass-border)',
  background: 'var(--card-bg)',
  color: 'var(--text)',
  fontSize: 13,
  cursor: 'pointer',
  colorScheme: 'inherit',
};

const navBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: CTRL_H,
  height: CTRL_H,
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--glass-border)',
  background: 'var(--card-bg)',
  color: 'var(--text)',
  cursor: 'pointer',
};

const grpLabel = {
  fontSize: 9,
  letterSpacing: '0.07em',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
  fontWeight: 700,
  marginBottom: 6,
  display: 'block',
};

/** Segmented control : boutons jointifs dans un conteneur arrondi. */
function Segmented({ options, value, onChange }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        height: CTRL_H,
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--radius-sm)',
        background: 'var(--card-bg)',
        overflow: 'hidden',
      }}
    >
      {options.map((o, i) => {
        const active = o.key === value;
        return (
          <button
            key={o.key}
            type="button"
            onClick={() => onChange(o.key)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '0 13px',
              border: 'none',
              borderLeft: i === 0 ? 'none' : '1px solid var(--glass-border)',
              background: active ? 'var(--accent)' : 'transparent',
              color: active ? '#fff' : 'var(--text)',
              fontSize: 12.5,
              fontWeight: active ? 600 : 500,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {o.icon}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function Group({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <span style={grpLabel}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{children}</div>
    </div>
  );
}

export default function ReportControls({
  viewMode,
  onViewMode,
  period,
  onPeriod,
  scope,
  availableScopes = ['self'],
  onScope,
  onShift,
  window: win,
  onWindowChange,
  showFilter = true,
  unit = 'hours',
  onUnit,
  presets = [],
  preset = 'custom',
  onPreset,
  ignoreWeekends = true,
  onIgnoreWeekends,
  teamDepth = 'all',
  onTeamDepth,
}) {
  return (
    <div
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 3,
        marginTop: 12,
        padding: '14px 16px',
        background: 'var(--card-bg)',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-card)',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      {/* Rangée 1 : Période + Granularité */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 20 }}>
        <Group label="Période">
          {presets.length > 0 && onPreset && (
            <select value={preset} style={{ ...field, fontWeight: 600, minWidth: 180 }} onChange={(e) => onPreset(e.target.value)}>
              {presets.map((p) => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
              <option value="custom" disabled>Personnalisé…</option>
            </select>
          )}
          <div style={{ display: 'inline-flex', gap: 4 }}>
            <button type="button" style={navBtn} title="Période précédente" onClick={() => onShift(-1)}>
              <ChevronLeft size={16} />
            </button>
            <button type="button" style={navBtn} title="Période suivante" onClick={() => onShift(1)}>
              <ChevronRight size={16} />
            </button>
          </div>
          {win && onWindowChange && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <DatePicker
                ariaLabel="Date de début"
                value={win.from}
                max={win.to}
                required
                style={{ ...field, width: 'auto' }}
                onChange={(val) => val && onWindowChange({ from: val, to: win.to })}
              />
              <span style={{ color: 'var(--text-muted)' }}>→</span>
              <DatePicker
                ariaLabel="Date de fin"
                value={win.to}
                min={win.from}
                required
                style={{ ...field, width: 'auto' }}
                onChange={(val) => val && onWindowChange({ from: win.from, to: val })}
              />
            </div>
          )}
        </Group>

        <div style={{ marginLeft: 'auto' }}>
          <Group label="Granularité">
            <Segmented options={PERIODS} value={period} onChange={onPeriod} />
          </Group>
        </div>
      </div>

      {/* Rangée 2 : Affichage + Périmètre */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 20 }}>
        <Group label="Affichage">
          <Segmented
            value={viewMode}
            onChange={onViewMode}
            options={[
              { key: 'scheduled', label: 'Planifié', icon: <CalendarRange size={13} /> },
              { key: 'logged', label: 'Saisi', icon: <CalendarClock size={13} /> },
            ]}
          />
          {onUnit && (
            <Segmented
              value={unit}
              onChange={onUnit}
              options={[
                { key: 'hours', label: 'Heures', icon: <Clock size={13} /> },
                { key: 'days', label: 'Jours', icon: <CalendarDays size={13} /> },
              ]}
            />
          )}
          {onIgnoreWeekends && (
            <button
              type="button"
              onClick={() => onIgnoreWeekends(!ignoreWeekends)}
              title="Ignorer les week-ends (samedi/dimanche)"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                height: CTRL_H,
                padding: '0 13px',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--glass-border)',
                background: ignoreWeekends ? 'var(--accent)' : 'var(--card-bg)',
                color: ignoreWeekends ? '#fff' : 'var(--text)',
                fontSize: 12.5,
                fontWeight: ignoreWeekends ? 600 : 500,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              <CalendarOff size={13} />
              Ignorer les week-ends
            </button>
          )}
        </Group>

        {availableScopes.length > 1 && (
          <div style={{ marginLeft: 'auto' }}>
            <Group label="Périmètre">
              <Segmented
                value={scope}
                onChange={onScope}
                options={availableScopes.map((s) => ({ key: s, label: SCOPE_LABELS[s] ?? s }))}
              />
              {scope === 'team' && onTeamDepth && (
                <Segmented
                  value={teamDepth}
                  onChange={onTeamDepth}
                  options={[
                    { key: 'direct', label: 'Directs' },
                    { key: 'all', label: '+ Descendants' },
                  ]}
                />
              )}
            </Group>
          </div>
        )}
      </div>

      {/* Rangée 3 : Filtres */}
      {showFilter && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 20 }}>
          <Group label="Filtres">
            <FilterBar scope="schedule" />
          </Group>
        </div>
      )}

      {/* Ticket #36 — le périmètre prime sur les filtres : en « Moi », le
          backend restreint déjà aux données de l'utilisateur, un filtre sur une
          autre personne / une équipe ne peut donc rien remonter. Sans ce
          rappel, l'écran passe pour « le rapport ignore mes filtres ». */}
      {scope === 'self' && <ScopeNotice availableScopes={availableScopes} />}
    </div>
  );
}

/** Rappel du périmètre « Moi » (ticket #36). */
function ScopeNotice({ availableScopes }) {
  const canWiden = availableScopes.length > 1;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        padding: '8px 12px',
        borderRadius: 'var(--radius-sm)',
        border: '1px solid var(--glass-border)',
        background: 'var(--glass)',
        color: 'var(--text-muted)',
        fontSize: 12.5,
        lineHeight: 1.45,
      }}
    >
      <Info size={14} style={{ flexShrink: 0, marginTop: 1 }} />
      <span>
        Périmètre <strong style={{ color: 'var(--text)' }}>Moi</strong> : le rapport ne porte que
        sur vos propres données. Un filtre sur une autre personne ou une équipe ne remontera rien.
        {canWiden
          ? ' Changez de périmètre ci-dessus pour élargir.'
          : ' Votre rôle ne permet pas d’élargir le périmètre.'}
      </span>
    </div>
  );
}
